import { useCallback, useEffect, useRef, useState } from 'react';

// Whole-component enter animation (the chosen "Direct"): SOLO(grey) + mint pill
// fade in (the pill fades in as a circle and expands to full width); the tabs reel
// up from the bottom and settle on the centre item; the UNITED frame expands
// straight to its rest height; the lines fade + rise in. A single rAF-driven
// progress (0→1) is derived into per-element state. `replay()` re-runs it.

const INTRO_DURATION_MS = 1200;

export interface IntroValues {
  active: boolean;
  soloOpacity: number;
  railPillOpacity: number;
  railPillExpand: number;
  railReel: number;
  unitedHeight: number;
  unitedOpacity: number;
  messagesOpacity: number;
  messagesRise: number;
}

interface IntroDims {
  wrapperHeight: number;
  restHeight: number;
}

interface Chatteaser3Intro {
  replay: () => void;
  introProgress: number;
  isIntroActive: boolean;
}

export function useChatteaser3Intro(): Chatteaser3Intro {
  const [introProgress, setIntroProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef(0);

  const stop = useCallback((): void => {
    if (rafRef.current == null) return;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const replay = useCallback((): void => {
    stop();
    setIntroProgress(0);
    startRef.current = performance.now();
    const tick = (now: number): void => {
      const progress = Math.min(1, (now - startRef.current) / INTRO_DURATION_MS);
      setIntroProgress(progress);
      rafRef.current = progress < 1 ? requestAnimationFrame(tick) : null;
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [stop]);

  useEffect(() => {
    const introFrame = requestAnimationFrame(replay);
    return () => {
      cancelAnimationFrame(introFrame);
      stop();
    };
  }, [replay, stop]);

  return {
    replay,
    introProgress,
    isIntroActive: introProgress < 1,
  };
}

function ramp(progress: number, from: number, to: number): number {
  if (to <= from) return progress >= to ? 1 : 0;
  return Math.max(0, Math.min(1, (progress - from) / (to - from)));
}

function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function deriveIntro(progress: number, dims: IntroDims): IntroValues {
  return {
    active: progress < 1,
    // SOLO fades in WITH the content, not before it.
    soloOpacity: ramp(progress, 0.3, 0.7),
    railPillOpacity: ramp(progress, 0, 0.25),
    railPillExpand: easeOut(ramp(progress, 0.2, 0.55)),
    railReel: easeOut(ramp(progress, 0.2, 0.78)),
    unitedHeight: dims.restHeight * easeOut(ramp(progress, 0.2, 0.7)),
    unitedOpacity: ramp(progress, 0.2, 0.4),
    messagesOpacity: ramp(progress, 0.55, 0.85),
    messagesRise: (1 - easeOut(ramp(progress, 0.55, 0.9))) * 24,
  };
}
